/** @jsxImportSource @emotion/react */
import { Formik } from 'formik'
import { isOrganization } from 'helpers/followUpProvidersHelper'
import moment from 'moment'
import * as Yup from 'yup'
import { useEffect, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Select from 'react-select'

import PageIcon from 'assets/img/page-icon.svg'
import { Order } from 'store/types'
import { FollowUpNamespace } from 'store/types'
import {
  useSetOrderFollowUpDateMutation,
  useUpdateFollowUpCareStatusMutation,
  useUpdateFollowUpCarePhysicianStatusMutation,
  useUpdateFollowUpCareScheduledDateMutation,
  useMarkFollowUpCareReferralLetterSentMutation,
} from 'store/services/labOrder'
import { useSendLabNoteMutation } from 'store/services/notes'
import { confirm } from 'components/molecules/Confirmation'
import Toast from 'components/atoms/Toast'
import DateTimePicker from '../../../atoms/DateTimePicker'
import { FollowUpForm, TimeWrapper } from './FollowUpForm'
import { Notes } from './Notes';

const formValidation = Yup.object().shape({
  followUpDate: Yup.date().required('Required'),
  notes: Yup.string(),
  humanFollowUpRequired: Yup.boolean(),
  humanFollowUpReason: Yup.string().when('humanFollowUpRequired', {
    is: true,
    then: Yup.string().trim().required('Required when human follow-up is selected'),
  }),
})

const FOLLOW_UP_CARE_STATUS_OPTIONS = [
  { value: 'PENDING', label: 'New' },
  { value: 'SENT', label: 'Sent' },
  { value: 'CONFIRMED', label: 'Follow-up' },
  { value: 'DONE', label: 'Complete' },
]

const PHYSICIAN_STATUS_OPTIONS = [
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'CLOSED', label: 'Closed' },
]

interface FollowUpCarePanelProps {
  order: Order
  onOrderUpdated?: () => void
}

export const FollowUpCarePanel = ({ order, onOrderUpdated }: FollowUpCarePanelProps) => {
  const [setOrderFollowUpDate] = useSetOrderFollowUpDateMutation()
  const [sendLabNote] = useSendLabNoteMutation()
  const [updateStatus] = useUpdateFollowUpCareStatusMutation()
  const [updatePhysicianStatus] = useUpdateFollowUpCarePhysicianStatusMutation()
  const [markReferralLetterSent] = useMarkFollowUpCareReferralLetterSentMutation()
  const [updateScheduledDate, { isLoading: isUpdatingScheduledDate }] =
    useUpdateFollowUpCareScheduledDateMutation()

  const sentChecked = !!order?.followUpCareSent
  const [statusSubmitting, setStatusSubmitting] = useState(false)
  const [physicianStatusSubmitting, setPhysicianStatusSubmitting] = useState(false)
  const [cancelSubmitting, setCancelSubmitting] = useState(false)
  const [selectedStatusOption, setSelectedStatusOption] = useState<{
    value: string
    label: string
  } | null>(null)
  const [selectedPhysicianStatusOption, setSelectedPhysicianStatusOption] = useState<{
    value: string
    label: string
  } | null>(null)
  const [scheduledDate, setScheduledDate] = useState<string | null>(
    order?.followUpCareScheduledDate
      ? moment.utc(order.followUpCareScheduledDate).format('YYYY-MM-DD')
      : null,
  )

  const followUps =
    order?.followUpsList?.filter((f) => f.namespace === FollowUpNamespace.FOLLOW_UP_CARE) ?? []
  const sortedFollowUps = [...followUps].sort((a, b) => moment(b.addedAt).diff(moment(a.addedAt)))
  const followUpDate = sortedFollowUps?.[0]?.date ? moment(sortedFollowUps[0].date).toDate() : ''
  const latestFollowUp = sortedFollowUps?.[0]

  const isInWorkflow = order?.followUpCarePhysicianId != null
  const isDone = order?.state?.FOLLOW_UP_CARE === 'DONE'

  const currentStatusOption =
    selectedStatusOption ??
    FOLLOW_UP_CARE_STATUS_OPTIONS.find((o) => {
      const state = order?.state?.FOLLOW_UP_CARE
      if (state === 'PENDING') return o.value === 'PENDING'
      if (state === 'SENT') return o.value === 'SENT'
      if (state === 'CONFIRMED') return o.value === 'CONFIRMED'
      if (state === 'DONE') return o.value === 'DONE'
      return o.value === 'PENDING'
    }) ??
    FOLLOW_UP_CARE_STATUS_OPTIONS[0]

  const currentPhysicianStatusOption =
    selectedPhysicianStatusOption ??
    (order?.followUpCarePhysicianStatus != null
      ? PHYSICIAN_STATUS_OPTIONS.find((o) => o.value === order.followUpCarePhysicianStatus) ?? null
      : null)

  const physicianStatusValue = order?.followUpCarePhysicianStatus

  const showScheduledDate = isDone && physicianStatusValue === 'SCHEDULED'

  useEffect(() => {
    setScheduledDate(
      order?.followUpCareScheduledDate
        ? moment.utc(order.followUpCareScheduledDate).format('YYYY-MM-DD')
        : null,
    )
  }, [order?.followUpCareScheduledDate])

  const handleSentCheck = async (checked: boolean) => {
    try {
      await markReferralLetterSent({
        orderId: Number(order.id),
        sent: checked,
      }).unwrap()
      Toast({
        type: 'success',
        label: checked ? 'Referral letter marked as sent.' : 'Referral letter marked as not sent.',
      })
      onOrderUpdated?.()
    } catch {
      Toast({ type: 'error', label: 'Failed to update.' })
    }
  }

  const handleStatusSubmit = async () => {
    const option = currentStatusOption
    if (!option) return
    setStatusSubmitting(true)
    try {
      await updateStatus({
        orderId: Number(order.id),
        status: option.value,
      }).unwrap()
      setSelectedStatusOption(null)
      Toast({ type: 'success', label: 'Status updated.' })
      onOrderUpdated?.()
    } catch {
      Toast({ type: 'error', label: 'Failed to update status.' })
    } finally {
      setStatusSubmitting(false)
    }
  }

  const handlePhysicianStatusSubmit = async () => {
    const option = currentPhysicianStatusOption
    if (!option) return
    setPhysicianStatusSubmitting(true)
    try {
      await updatePhysicianStatus({
        orderId: Number(order.id),
        status: option.value,
      }).unwrap()
      setSelectedPhysicianStatusOption(null)
      Toast({ type: 'success', label: 'Physician status updated.' })
      onOrderUpdated?.()
    } catch {
      Toast({ type: 'error', label: 'Failed to update physician status.' })
    } finally {
      setPhysicianStatusSubmitting(false)
    }
  }

  const handleScheduledDateChange = async (date: Date | null) => {
    const formatted = date ? moment(date).format('YYYY-MM-DD') : null
    setScheduledDate(formatted)
    try {
      await updateScheduledDate({
        orderId: Number(order.id),
        scheduledDate: formatted,
      }).unwrap()
      Toast({ type: 'success', label: 'Scheduled date updated.' })
      onOrderUpdated?.()
    } catch {
      Toast({ type: 'error', label: 'Failed to update scheduled date.' })
    }
  }

  const handleCancel = async () => {
    try {
      await confirm('', {
        title: 'Cancel follow-up care',
        description:
          'This will remove the order from the follow-up care workflow. The assigned follow-up provider will be cleared. Continue?',
        yesAction: 'Yes, cancel',
        noAction: 'No',
      })
    } catch {
      return
    }
    setCancelSubmitting(true)
    try {
      await updateStatus({
        orderId: Number(order.id),
        status: 'CANCELLED',
      }).unwrap()
      Toast({ type: 'success', label: 'Follow-up care cancelled.' })
      onOrderUpdated?.()
    } catch {
      Toast({ type: 'error', label: 'Failed to cancel follow-up care.' })
    } finally {
      setCancelSubmitting(false)
    }
  }

  if (!isInWorkflow) return null

  return (
    <div
      className="w-100 mb-2 ml-2"
      css={{
        background: 'white',
        borderRadius: '5px',
        border: '0.6px solid rgb(200, 200, 200)',
      }}>
      <div className="p-4">
        <h6 className="mb-3" css={{ fontWeight: 600, color: '#374151' }}>
          <i className="fas fa-user-nurse" css={{ marginRight: '0.5rem' }} />
          Follow-Up Care
        </h6>

        {order?.followUpCarePhysician && (
          <div
            className="mb-3 p-2"
            css={{
              background: '#f8f9fa',
              borderRadius: 4,
              fontSize: '14px',
            }}>
            <span css={{ fontWeight: 600, color: '#374151' }}>Follow-up provider: </span>
            <span>
              {isOrganization(order.followUpCarePhysician.providerType)
                ? order.followUpCarePhysician.organizationName
                : [
                    order.followUpCarePhysician.firstName,
                    order.followUpCarePhysician.middleName,
                    order.followUpCarePhysician.lastName,
                  ]
                    .filter(Boolean)
                    .join(' ')}
              {order.followUpCarePhysician.npi != null &&
                order.followUpCarePhysician.npi !== '' && (
                  <span className="text-muted ml-1">(NPI: {order.followUpCarePhysician.npi})</span>
                )}
            </span>
          </div>
        )}

        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="follow-up-care-sent"
            label="Sent referral letter to follow up provider - along with report, order and office notes."
            checked={sentChecked}
            onChange={(e) => handleSentCheck(e.target.checked)}
          />
        </Form.Group>

        <div className="row mt-2" css={{ background: '#F5F5F5', borderRadius: 4 }}>
          <div className="col-lg-4 pt-3">
            <Formik
              enableReinitialize
              validationSchema={formValidation}
              initialValues={{
                followUpDate,
                notes: '',
                humanFollowUpRequired: Boolean(order?.patient?.voiceOptOut) || (latestFollowUp?.humanFollowUpRequired ?? false),
                humanFollowUpReason:
                  latestFollowUp?.humanFollowUpReason ||
                  (order?.patient?.voiceOptOut ? 'Patient opted out of automated voice calls' : ''),
              }}
              onSubmit={async (values, { resetForm }) => {
                try {
                  await Promise.all([
                    setOrderFollowUpDate({
                      orderId: Number(order.id),
                      namespace: FollowUpNamespace.FOLLOW_UP_CARE,
                      date: moment(values.followUpDate).format('YYYY-MM-DD'),
                      comment: values.notes,
                      humanFollowUpRequired: values.humanFollowUpRequired,
                      humanFollowUpReason: values.humanFollowUpRequired
                        ? values.humanFollowUpReason
                        : null,
                    }).unwrap(),
                    sendLabNote({
                      orderId: Number(order.id),
                      text: `${values.notes} (follow-up: ${moment(values.followUpDate).format(
                        'MM/DD/YYYY',
                      )})`,
                      type: 'scheduling',
                    }).unwrap(),
                  ])
                  resetForm()
                  Toast({ type: 'success', label: 'Notes and follow-up date updated.' })
                  onOrderUpdated?.()
                } catch {
                  Toast({ type: 'error', label: 'Failed to save.' })
                }
              }}>
              {({ values, setFieldValue, errors, touched }) => (
                <FollowUpForm
                  values={values}
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                  forceHumanFollowUp={Boolean(order?.patient?.voiceOptOut)}
                />
              )}
            </Formik>
          </div>
          <div className="col-lg-8 p-3">
            <Notes
              followUps={sortedFollowUps}
              orderId={Number(order.id)}
              isFollowUpSection={true}
              isAutoSmsAvailable={false}
            />
          </div>
        </div>

        <div className="row mt-3">
          <div>
            <label style={{ marginTop: 10, marginRight: 10, fontWeight: 500 }}>Status:</label>
          </div>
          <div>
            <Select
              className="basic-single"
              classNamePrefix="select"
              options={FOLLOW_UP_CARE_STATUS_OPTIONS}
              value={currentStatusOption}
              onChange={(option) => setSelectedStatusOption(option)}
              isSearchable={false}
              styles={{
                control: (base) => ({ ...base, minWidth: 140, minHeight: 38 }),
              }}
            />
          </div>
          <div>
            <Button
              variant="outline-primary"
              className={'ml-3 mx-1'}
              disabled={statusSubmitting}
              onClick={handleStatusSubmit}>
              {statusSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
            <Button variant="outline-danger" disabled={cancelSubmitting} onClick={handleCancel}>
              {cancelSubmitting ? 'Cancelling...' : 'Cancel follow-up care'}
            </Button>
          </div>
        </div>
        {isDone && (
          <>
            <div className="row mt-1">
              <div>
                <label style={{ marginTop: 10, marginRight: 10, fontWeight: 500 }}>
                  Referral status:
                </label>
              </div>
              <div>
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  options={PHYSICIAN_STATUS_OPTIONS}
                  value={currentPhysicianStatusOption}
                  onChange={(option) => setSelectedPhysicianStatusOption(option)}
                  isSearchable={false}
                  placeholder="Select..."
                  styles={{
                    control: (base) => ({ ...base, minWidth: 140, minHeight: 38 }),
                  }}
                />
              </div>
              <div>
                <Button
                  variant="primary"
                  className={'ml-3 mx-1'}
                  disabled={physicianStatusSubmitting || !currentPhysicianStatusOption}
                  onClick={handlePhysicianStatusSubmit}>
                  {physicianStatusSubmitting ? 'Submitting...' : 'Update'}
                </Button>
              </div>
            </div>
            <div className="row mt-2">
              {showScheduledDate && (
                <div>
                  <TimeWrapper
                    className="d-flex flex-row align-items-center"
                    css={{ minWidth: 300 }}>
                    <label
                      style={{ margin: 0, marginRight: 10, fontWeight: 500 }}
                      htmlFor="scheduledDate">
                      Scheduled Date:
                    </label>
                    <DateTimePicker
                      value={scheduledDate ? moment(scheduledDate, 'YYYY-MM-DD').toDate() : null}
                      handleChange={(date) => handleScheduledDateChange(date)}
                      showTimeSelect={false}
                      isClearable
                      dateFormat="MMMM d, yyyy"
                      disabled={isUpdatingScheduledDate}
                    />
                  </TimeWrapper>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
